import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { BRAND, BRAND_SUB } from "@/lib/constants";

const LINKS = [
  { to: "/properties", label: "Properties", id: "properties" },
  { to: "/completed", label: "Completed deals", id: "completed" },
  { to: "/buy", label: "Buyers", id: "buyers" },
  { to: "/sell", label: "Sellers", id: "sellers" },
];

export const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
    <span className="grid h-9 w-9 place-items-center bg-[#121417] text-[#FAF9F6] font-display text-lg transition-colors duration-300 group-hover:bg-[#9F7E44]">F</span>
    <span className="leading-none">
      <span className="block font-display text-xl tracking-tight">{BRAND}</span>
      <span className="block font-plex text-[10px] uppercase tracking-[0.25em] text-[#9F7E44] mt-1">{BRAND_SUB}</span>
    </span>
  </Link>
);

const linkCls = ({ isActive }) =>
  `text-sm transition-colors duration-200 ${isActive ? "text-[#121417]" : "text-[#57534E] hover:text-[#9F7E44]"}`;

const AccountActions = ({ onDone = () => {} }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (user) {
    return (
      <>
        <Link to={user.role === "admin" ? "/admin" : "/portal"} onClick={onDone} data-testid="nav-btn-client-portal"
          className="text-sm px-4 py-2 border border-[#121417] hover:bg-[#121417] hover:text-white transition-colors">
          {user.role === "admin" ? "Admin desk" : "My portal"}
        </Link>
        <button data-testid="nav-btn-logout" onClick={async () => { await logout(); onDone(); nav("/"); }}
          className="text-sm text-[#57534E] hover:text-[#9F7E44] inline-flex items-center gap-1.5">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </>
    );
  }
  return (
    <>
      <Link to="/auth" onClick={onDone} data-testid="nav-btn-signin" className="text-sm text-[#57534E] hover:text-[#9F7E44]">Sign in</Link>
      <Link to="/auth?mode=register" onClick={onDone} data-testid="nav-btn-register"
        className="text-sm px-5 py-2.5 bg-[#9F7E44] text-white hover:bg-[#866835] transition-colors">Create account</Link>
    </>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FBFBF9]/90 border-b border-[#E4E1D9]/80">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between gap-6">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-link-${l.id}`}>{l.label}</NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-5"><AccountActions /></div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="lg:hidden p-2" data-testid="nav-mobile-menu-btn" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[#FBFBF9] w-72">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="flex flex-col gap-6 mt-10">
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="font-display text-2xl" data-testid={`mobile-nav-link-${l.id}`}>{l.label}</NavLink>
              ))}
              <div className="h-px bg-[#E4E1D9]" />
              <div className="flex flex-col items-start gap-4"><AccountActions onDone={() => setOpen(false)} /></div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
